import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';
import type { OpenRovoPayload } from '../../custom-ui/bridge/types';
import { getIsRovoEnabled } from '../../web-client/queries/getIsRovoEnabled';

// ─── openRovo ────────────────────────────────────────────────────────────────

type OpenRovoContext = MakeBridgeContext<'bridgeConfig'>;

/**
 * Opens the Rovo chat panel by delegating to the host-provided `openRovo`
 * callback on `BridgeConfig`.
 *
 * Returns `false` when the host has not provided the callback;
 * returns the callback's own return value when provided (defaulting to `true`
 * when the callback returns `undefined`).
 */
export const openRovo: TBridgeMethod<OpenRovoContext, OpenRovoPayload, boolean> = async (
	context,
	payload,
) => {
	const cb = context.bridgeConfig.openRovo;
	if (!cb) {
		return false;
	}
	const result = await cb(payload);
	return result ?? true;
};

// ─── isRovoEnabled ───────────────────────────────────────────────────────────

type IsRovoEnabledContext = MakeBridgeContext<'resolverContext', 'relayEnvironment'>;

/**
 * Returns whether Rovo is enabled for the current site.
 *
 * Returns `false` immediately when `cloudId` is absent on the resolver context
 * or when the Relay environment is unavailable.
 */
export const isRovoEnabled: TBridgeMethod<IsRovoEnabledContext, void, boolean> = async (
	context,
) => {
	const { cloudId } = context.resolverContext;
	if (!cloudId) {
		return false;
	}
	return getIsRovoEnabled({ cloudId, relayEnvironment: context.services.relayEnvironment });
};
